import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, X, Send, Trash2, Check, Loader2, CheckCircle2, XCircle, Building2, Package, Container as ContainerIcon, CalendarClock, ExternalLink, Mic, Square, History, Search, Plus, MessageSquare, Users, Copy, ChevronDown, GripVertical, LucideIcon } from 'lucide-react';
import { searchConversations, StoredConversation } from './conversationStore';
import { useAssistant } from './useAssistant';
import { useVoiceDictation } from './useVoiceDictation';
import { AssistantComplexity, AssistantToolResult } from '../../interfaces/Assistant';
import { EntityKind, extractEntityRefs, openEntity } from './entityRefs';
import { extractArtifacts } from './artifacts';
import ArtifactCard from './ArtifactCard';
import { OptionService } from '../../services/OptionService';

const COMPLEXITY_LEVELS: { id: AssistantComplexity; label: string; title: string }[] = [
  { id: 'simple', label: 'Simplu', title: 'Răspunsuri scurte · gpt-5.6-luna · 5k tokeni' },
  { id: 'normal', label: 'Normal', title: 'Echilibrat · Grok 4.6 · 12k tokeni' },
  { id: 'advanced', label: 'Avansat', title: 'Detaliat · Grok 4.6 · 24k tokeni' },
];

/** Slug-ul setării din tabelul `option` care controlează vizibilitatea chatului AI. */
export const AI_CHAT_VISIBLE_SLUG = 'ai-chat-visible';
/** Eveniment intern emis de pagina de setări la schimbarea vizibilității (reflectă fără refresh). */
export const AI_CHAT_VISIBILITY_EVENT = 'assistant:visibility';
/** Poziția panoului salvată după tragere, ca offseturi față de colțul dreapta-jos al ecranului. */
const ASSISTANT_POS_KEY = 'assistant-pos';
/** Dimensiunea panoului salvată după redimensionare. */
const ASSISTANT_SIZE_KEY = 'assistant-size';
/** Poziția butonului plutitor (offseturi right/bottom), salvată după tragere. */
const ASSISTANT_FAB_POS_KEY = 'assistant-fab-pos';
/** Diametrul butonului plutitor (h-12 = 48px), folosit la limitarea în viewport. */
const FAB_SIZE = 48;
const DEFAULT_W = 500;
const DEFAULT_H = 560;
const MIN_W = 380;
const MIN_H = 420;

const ENTITY_ICON: Record<EntityKind, LucideIcon> = {
  client: Building2,
  booking: Package,
  container: ContainerIcon,
  activity: CalendarClock,
  meeting: Users,
};

const EntityChips: React.FC<{ results?: AssistantToolResult[] }> = ({ results }) => {
  const refs = extractEntityRefs(results);
  if (refs.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {refs.map((ref, i) => {
        const Icon = ENTITY_ICON[ref.kind];
        const clickable = ref.kind === 'booking' || ref.kind === 'client' || (ref.kind === 'activity' && !!ref.id) || (ref.kind === 'container' && !!ref.bookingId) || (ref.kind === 'meeting' && !!ref.id);
        return (
          <button
            key={`${ref.kind}-${ref.id ?? ref.label}-${i}`}
            onClick={() => clickable && openEntity(ref)}
            disabled={!clickable}
            title={clickable ? `Deschide ${ref.label}` : ref.label}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors ${
              clickable
                ? 'cursor-pointer border-gray-200 bg-white text-gray-600 shadow-sm hover:border-primary/60 hover:text-darkprimary dark:border-dm-border dark:bg-dm-surface dark:text-dm-text dark:hover:border-primary/60'
                : 'cursor-default border-gray-200 bg-gray-50 text-gray-400 dark:border-dm-border dark:bg-dm-bg dark:text-dm-text-muted'
            }`}
          >
            <Icon size={12} />
            <span className="max-w-[160px] truncate">{ref.label}</span>
            {clickable && <ExternalLink size={10} className="opacity-60" />}
          </button>
        );
      })}
    </div>
  );
};

// Componente Markdown adaptate la lățimea îngustă a drawerului:
// tabelele (dacă totuși apar) primesc scroll orizontal și text mic, ca să nu spargă layout-ul.
const markdownComponents = {
  table: ({ node, ...props }: any) => (
    <div className="my-1 overflow-x-auto">
      <table className="text-[11px]" {...props} />
    </div>
  ),
  th: ({ node, ...props }: any) => <th className="whitespace-nowrap px-1.5 py-0.5" {...props} />,
  td: ({ node, ...props }: any) => <td className="px-1.5 py-0.5 align-top" {...props} />,
  a: ({ node, ...props }: any) => <a className="break-all underline" target="_blank" rel="noreferrer" {...props} />,
  code: ({ node, ...props }: any) => <code className="whitespace-pre-wrap break-words" {...props} />,
};

/** Etichete prietenoase pentru tool-uri — numele tehnice (queryData, searchClients) nu spun nimic utilizatorului. */
const TOOL_LABELS: Record<string, string> = {
  searchClients: 'Căutare clienți',
  searchBookings: 'Căutare bookinguri',
  getClient: 'Detalii client',
  getClientContacts: 'Contacte client',
  getClientBookings: 'Bookinguri client',
  getBooking: 'Detalii booking',
  getContainer: 'Detalii container',
  queryData: 'Interogare date',
  salesVolumeReport: 'Raport TEU sales',
  freightProfitReport: 'Profit navlu',
  generateReport: 'Raport generat',
  searchWeb: 'Căutare pe internet',
  searchMeetings: 'Căutare ședințe',
  getMeeting: 'Detalii ședință',
  getMeetingTranscript: 'Transcript ședință',
  compareMeetingToActivities: 'Ședință vs. activități',
  createActivity: 'Activitate creată',
  updateActivity: 'Activitate actualizată',
  updateClient: 'Client actualizat',
  rememberFact: 'Reținut în memorie',
  forgetMemory: 'Șters din memorie',
  listMemory: 'Memorie consultată',
};

const ToolPills: React.FC<{ results?: AssistantToolResult[] }> = ({ results }) => {
  if (!results || results.length === 0) return null;
  // Grupăm rulările repetate ale aceluiași tool (ex. 3 interogări) într-un singur pill cu „×3".
  const grouped: { toolName: string; ok: boolean; count: number; errorMessage?: string | null }[] = [];
  const byKey: Record<string, (typeof grouped)[number]> = {};
  for (const r of results) {
    const key = `${r.toolName}|${r.ok}`;
    if (byKey[key]) {
      byKey[key].count += 1;
    } else {
      const entry = { toolName: r.toolName, ok: r.ok, count: 1, errorMessage: r.errorMessage };
      byKey[key] = entry;
      grouped.push(entry);
    }
  }
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {grouped.map((r, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            r.ok ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
          }`}
          title={r.errorMessage || r.toolName}
        >
          {r.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
          {TOOL_LABELS[r.toolName] || r.toolName}
          {r.count > 1 && <span className="opacity-70">×{r.count}</span>}
        </span>
      ))}
    </div>
  );
};

/** Sugestii de start în funcție de pagina curentă (când conversația e goală). */
function starterSuggestions(entityType?: string): { label: string; prompt: string }[] {
  if (entityType === 'client') {
    return [
      { label: 'Rezumat client', prompt: 'Dă-mi un rezumat al clientului de pe această pagină' },
      { label: 'Bookingurile lui', prompt: 'Arată-mi bookingurile clientului de pe această pagină' },
      { label: 'Creează activitate', prompt: 'Creează o activitate pentru clientul de pe această pagină' },
    ];
  }
  if (entityType === 'booking') {
    return [
      { label: 'Detalii booking', prompt: 'Dă-mi detaliile bookingului de pe această pagină' },
      { label: 'Containerele', prompt: 'Ce containere are bookingul de pe această pagină?' },
    ];
  }
  return [
    { label: 'Caută un client', prompt: 'Caută clientul ' },
    { label: 'Caută un booking', prompt: 'Caută bookingul ' },
    { label: 'Oferte automate active', prompt: 'Care clienți au setarea de oferte automate activă?' },
    { label: 'Facturi restante', prompt: 'Ce facturi restante avem acum?' },
    { label: 'Unde e nava…', prompt: 'Unde se află nava ' },
  ];
}

/** Etichetă relativă pentru un timestamp („acum", „5 min", „ieri", „3 z"). */
function relativeTime(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'acum';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'ieri';
  if (d < 30) return `${d} z`;
  return new Date(ts).toLocaleDateString('ro-RO');
}

/** mm:ss rămas până la un timestamp ISO; null dacă a expirat. */
function remainingLabel(expiresAt?: string, now?: number): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - (now ?? Date.now());
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

export const AssistantPanel: React.FC = () => {
  const a = useAssistant();
  const [draft, setDraft] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [sendStart, setSendStart] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);

  const copyMessage = (id: string, content: string) => {
    navigator.clipboard
      .writeText(content)
      .then(() => {
        setCopiedId(id);
        setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1500);
      })
      .catch(() => undefined);
  };

  // --- Mutare (drag pe header) și redimensionare (colțul stânga-sus) -----------
  // Panoul e ancorat de colțul DREAPTA-JOS: poziția = offseturi right/bottom față de ecran.
  // Așa, redimensionarea din colțul stânga-sus crește panoul în sus/stânga fără să miște ancora.
  const panelRef = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<{ right: number; bottom: number } | null>(() => {
    try {
      const raw = localStorage.getItem(ASSISTANT_POS_KEY);
      const p = raw ? (JSON.parse(raw) as { right?: unknown; bottom?: unknown }) : null;
      return p && typeof p.right === 'number' && typeof p.bottom === 'number' ? { right: p.right, bottom: p.bottom } : null;
    } catch {
      return null;
    }
  });
  const [size, setSize] = useState<{ w: number; h: number } | null>(() => {
    try {
      const raw = localStorage.getItem(ASSISTANT_SIZE_KEY);
      const s = raw ? (JSON.parse(raw) as { w?: unknown; h?: unknown }) : null;
      return s && typeof s.w === 'number' && typeof s.h === 'number' ? { w: Math.max(MIN_W, s.w), h: Math.max(MIN_H, s.h) } : null;
    } catch {
      return null;
    }
  });
  const [dragging, setDragging] = useState(false);

  const clampOffsets = (right: number, bottom: number, w: number, h: number) => ({
    right: Math.min(Math.max(8, right), Math.max(8, window.innerWidth - w - 8)),
    bottom: Math.min(Math.max(8, bottom), Math.max(8, window.innerHeight - h - 8)),
  });

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    // Butoanele din header rămân clickabile — tragerea pornește doar din zonele libere.
    if ((e.target as HTMLElement).closest('button, input')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origRight = window.innerWidth - rect.right;
    const origBottom = window.innerHeight - rect.bottom;
    let last: { right: number; bottom: number } | null = null;
    setDragging(true);
    const onMove = (ev: PointerEvent) => {
      // Scriem direct pe element, fără setState: zero re-render-uri pe durata tragerii.
      last = clampOffsets(origRight - (ev.clientX - startX), origBottom - (ev.clientY - startY), rect.width, rect.height);
      el.style.right = `${last.right}px`;
      el.style.bottom = `${last.bottom}px`;
      el.style.left = 'auto';
      el.style.top = 'auto';
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setDragging(false);
      if (last) {
        localStorage.setItem(ASSISTANT_POS_KEY, JSON.stringify(last));
        setPos(last);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  /** Redimensionare din colțul stânga-sus: colțul dreapta-jos rămâne fix. */
  const onResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = rect.width;
    const startH = rect.height;
    const right = window.innerWidth - rect.right;
    const bottom = window.innerHeight - rect.bottom;
    let last: { w: number; h: number } | null = null;
    const onMove = (ev: PointerEvent) => {
      last = {
        w: Math.min(Math.max(MIN_W, startW - (ev.clientX - startX)), window.innerWidth - right - 8),
        h: Math.min(Math.max(MIN_H, startH - (ev.clientY - startY)), window.innerHeight - bottom - 8),
      };
      el.style.width = `${last.w}px`;
      el.style.height = `${last.h}px`;
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (last) {
        localStorage.setItem(ASSISTANT_SIZE_KEY, JSON.stringify(last));
        setSize(last);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // --- Butonul plutitor (FAB) e și el mutabil ---------------------------------
  const fabRef = useRef<HTMLButtonElement>(null);
  const [fabPos, setFabPos] = useState<{ right: number; bottom: number } | null>(() => {
    try {
      const raw = localStorage.getItem(ASSISTANT_FAB_POS_KEY);
      const p = raw ? (JSON.parse(raw) as { right?: unknown; bottom?: unknown }) : null;
      return p && typeof p.right === 'number' && typeof p.bottom === 'number' ? { right: p.right, bottom: p.bottom } : null;
    } catch {
      return null;
    }
  });
  /** True dacă ultimul pointerdown pe FAB a fost o tragere — suprimă click-ul de deschidere. */
  const fabDraggedRef = useRef(false);

  const onFabPointerDown = (e: React.PointerEvent) => {
    const el = fabRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const origRight = window.innerWidth - rect.right;
    const origBottom = window.innerHeight - rect.bottom;
    let last: { right: number; bottom: number } | null = null;
    fabDraggedRef.current = false;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      // Sub 4px e considerat click, nu tragere — altfel orice click ar muta butonul cu un pixel.
      if (!fabDraggedRef.current && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      fabDraggedRef.current = true;
      last = clampOffsets(origRight - dx, origBottom - dy, rect.width, rect.height);
      el.style.right = `${last.right}px`;
      el.style.bottom = `${last.bottom}px`;
      el.style.left = 'auto';
      el.style.top = 'auto';
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      if (last) {
        localStorage.setItem(ASSISTANT_FAB_POS_KEY, JSON.stringify(last));
        setFabPos(last);
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  /** Revine la poziția și dimensiunea implicite (dublu-click pe header). */
  const resetPos = () => {
    setPos(null);
    setSize(null);
    localStorage.removeItem(ASSISTANT_POS_KEY);
    localStorage.removeItem(ASSISTANT_SIZE_KEY);
  };

  // Panoul rămâne în viewport la resize de fereastră.
  useEffect(() => {
    const clampNow = () => {
      const el = panelRef.current;
      if (el) {
        setPos((p) => {
          if (!p) return p;
          const c = clampOffsets(p.right, p.bottom, el.offsetWidth, el.offsetHeight);
          if (c.right === p.right && c.bottom === p.bottom) return p;
          localStorage.setItem(ASSISTANT_POS_KEY, JSON.stringify(c));
          return c;
        });
      }
      setFabPos((p) => {
        if (!p) return p;
        const c = clampOffsets(p.right, p.bottom, FAB_SIZE, FAB_SIZE);
        if (c.right === p.right && c.bottom === p.bottom) return p;
        localStorage.setItem(ASSISTANT_FAB_POS_KEY, JSON.stringify(c));
        return c;
      });
    };
    clampNow();
    window.addEventListener('resize', clampNow);
    return () => window.removeEventListener('resize', clampNow);
  }, [size, a.isOpen]);

  const historyResults: StoredConversation[] = historyOpen ? searchConversations(historyQuery) : [];

  // Vizibilitatea chatului, controlată din setarea `ai-chat-visible` (tabelul option).
  // null = încă neîncărcat (nu afișăm nimic, ca să evităm un flash dacă e ascuns).
  const [aiVisible, setAiVisible] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    new OptionService()
      .getOption(AI_CHAT_VISIBLE_SLUG)
      .then((o) => active && setAiVisible(o?.value !== 'false'))
      .catch(() => active && setAiVisible(true)); // la eroare/lipsă setare → vizibil implicit
    const onVis = (e: Event) => setAiVisible((e as CustomEvent).detail !== false);
    window.addEventListener(AI_CHAT_VISIBILITY_EVENT, onVis);
    return () => {
      active = false;
      window.removeEventListener(AI_CHAT_VISIBILITY_EVENT, onVis);
    };
  }, []);

  const openHistory = () => {
    a.refreshConversations();
    setHistoryQuery('');
    setHistoryOpen(true);
  };

  // Închide dropdown-ul de istoric la click în afara lui.
  useEffect(() => {
    if (!historyOpen) return;
    const onClick = (e: MouseEvent) => {
      if (historyRef.current && !historyRef.current.contains(e.target as Node)) setHistoryOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [historyOpen]);

  const dictationPrefixRef = useRef('');
  const voice = useVoiceDictation({
    onLive: (committed, partial) => {
      const tail = [committed, partial].filter((s) => s.trim()).join(committed && !committed.endsWith(' ') ? ' ' : '');
      setDraft(dictationPrefixRef.current + tail);
    },
    onResult: () => textareaRef.current?.focus(),
    onError: (msg) => setVoiceError(msg),
  });

  const handleMicToggle = () => {
    if (a.isSending) return;
    setVoiceError(null);
    if (voice.status === 'idle') {
      dictationPrefixRef.current = draft && !/\s$/.test(draft) ? `${draft} ` : draft;
      setDraft(dictationPrefixRef.current);
    }
    voice.toggle();
  };

  // Tick pentru countdown-ul de expirare al acțiunii în așteptare.
  useEffect(() => {
    if (!a.pendingAction) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [a.pendingAction]);

  // Toggle cu ⌘J / Ctrl+J, închidere cu Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        a.toggle();
      } else if (e.key === 'Escape' && a.isOpen) {
        if (historyOpen) {
          setHistoryOpen(false);
        } else if (a.isSending) {
          // Oprește trimiterea în curs și repune mesajul în input.
          const restored = a.cancelSend();
          if (restored) setDraft((prev) => (prev.trim() ? prev : restored));
        } else {
          a.close();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [a, historyOpen]);

  const atBottomRef = useRef(true);
  const programmaticScrollUntilRef = useRef(0);

  /** Derulează la ultimul mesaj. Evenimentele de scroll generate programatic sunt ignorate de handleScroll. */
  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    programmaticScrollUntilRef.current = Date.now() + 600;
    atBottomRef.current = true;
    setAtBottom(true);
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Auto-scroll la ultimul mesaj — doar dacă userul e deja aproape de capăt.
  // Citim din ref, nu din state: derularea lină emite evenimente de scroll care ar comuta
  // state-ul și ar re-declanșa efectul în buclă (scroll care „se zbate").
  useEffect(() => {
    if (!atBottomRef.current) return;
    scrollToBottom();
  }, [a.messages, a.isSending, a.liveSteps, a.liveMessage, a.liveNotes, a.pendingAction, scrollToBottom]);

  // Cronometru pentru indicatorul de procesare (afișat la răspunsurile care durează).
  useEffect(() => {
    if (!a.isSending) {
      setSendStart(null);
      return;
    }
    setSendStart(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [a.isSending]);

  const handleScroll = () => {
    // Ignoră evenimentele produse de derularea programatică (smooth scroll în curs).
    if (Date.now() < programmaticScrollUntilRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottomRef.current = nearBottom;
    setAtBottom(nearBottom);
  };

  // Auto-resize textarea: crește în funcție de numărul de rânduri scrise (până la max-h-32 = 128px).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [draft]);

  // La fiecare deschidere a drawerului, derulează imediat până jos (ultimul mesaj).
  useEffect(() => {
    if (!a.isOpen) return;
    const id = requestAnimationFrame(() => scrollToBottom(false));
    return () => cancelAnimationFrame(id);
  }, [a.isOpen, scrollToBottom]);

  const submit = () => {
    if (!draft.trim() || a.isSending) return;
    a.send(draft);
    setDraft('');
    atBottomRef.current = true;
    setAtBottom(true);
  };

  // Quick-reply: prompturile care se termină cu spațiu sunt „de completat" (pun textul în input);
  // restul se trimit direct.
  const handleQuick = (prompt: string) => {
    if (a.isSending) return;
    if (prompt.endsWith(' ')) {
      setDraft(prompt);
      textareaRef.current?.focus();
    } else {
      a.send(prompt);
      atBottomRef.current = true;
      setAtBottom(true);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter (sau ⌘/Ctrl+Enter) trimite; Shift+Enter = rând nou.
    // Ignorăm Enter-ul de finalizare a compunerii IME (e.nativeEvent.isComposing).
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  // Ascuns prin setare (sau încă neîncărcat) → nu randăm nici butonul, nici panoul.
  if (!aiVisible) return null;

  return createPortal(
    <>
      {!a.isOpen && (
        <button
          ref={fabRef}
          onPointerDown={onFabPointerDown}
          onClick={() => {
            // După o tragere, click-ul care urmează la eliberare nu trebuie să deschidă chatul.
            if (fabDraggedRef.current) {
              fabDraggedRef.current = false;
              return;
            }
            a.open();
          }}
          style={fabPos ? { right: fabPos.right, bottom: fabPos.bottom } : undefined}
          aria-label="Deschide Transport GPT"
          title="Click: deschide chatul · trage pentru a muta butonul"
          className="fixed bottom-5 right-5 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-darkprimary text-black shadow-lg ring-1 ring-black/10 transition-transform hover:scale-110 active:scale-95"
        >
          <Sparkles size={20} />
        </button>
      )}

      {a.isOpen && (
        <aside
          ref={panelRef}
          style={{
            ...(pos ? { right: pos.right, bottom: pos.bottom, left: 'auto', top: 'auto' } : {}),
            width: size?.w ?? DEFAULT_W,
            height: size?.h ?? DEFAULT_H,
          }}
          className="fixed bottom-4 right-4 z-[60] flex max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-2xl ring-1 ring-black/5 dark:border-dm-border dark:bg-dm-surface dark:ring-white/5"
        >
          {/* Colț de redimensionare (stânga-sus) — colțul dreapta-jos rămâne ancorat */}
          <div onPointerDown={onResizePointerDown} title="Trage pentru a redimensiona" className="group absolute left-0 top-0 z-20 h-5 w-5 cursor-nwse-resize">
            <span className="absolute left-1 top-1 block h-2.5 w-2.5 rounded-tl-md border-l-2 border-t-2 border-gray-300 transition-colors group-hover:border-darkprimary dark:border-dm-text-muted" />
          </div>
          {/* Header — și mâner de mutare: trage panoul oriunde, dublu-click revine la poziția implicită */}
          <div
            onPointerDown={onHeaderPointerDown}
            onDoubleClick={resetPos}
            title="Trage pentru a muta panoul · dublu-click: poziție și dimensiune implicite"
            className={`flex select-none touch-none items-center justify-between border-b border-gray-100 px-3 py-2.5 dark:border-dm-border ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
          >
            <div className="flex items-center gap-1.5">
              <GripVertical size={14} className="text-gray-300 dark:text-dm-text-muted" />
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-darkprimary text-black shadow-sm">
                <Sparkles size={15} />
              </span>
              <span className="ml-0.5 text-sm font-semibold text-gray-800 dark:text-dm-text">Transport GPT</span>
            </div>
            <div className="flex items-center gap-1">
              {/* Istoric conversații cu căutare */}
              <div ref={historyRef} className="relative">
                <button
                  onClick={() => (historyOpen ? setHistoryOpen(false) : openHistory())}
                  title="Istoric conversații"
                  className={`rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-dm-text-muted dark:hover:bg-dm-hover ${historyOpen ? 'bg-gray-100 text-gray-600 dark:bg-dm-hover' : ''}`}
                >
                  <History size={16} />
                </button>
                {historyOpen && (
                  <div className="absolute right-0 z-[70] mt-1 w-80 max-w-[calc(100vw-3rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-dm-border dark:bg-dm-surface">
                    <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-dm-border">
                      <Search size={14} className="text-gray-400 dark:text-dm-text-muted" />
                      <input
                        autoFocus
                        value={historyQuery}
                        onChange={(e) => setHistoryQuery(e.target.value)}
                        placeholder="Caută în conversații…"
                        className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-dm-text dark:placeholder:text-dm-text-muted"
                      />
                    </div>
                    <button
                      onClick={() => {
                        a.newConversation();
                        setHistoryOpen(false);
                      }}
                      className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm font-medium text-primary hover:bg-gray-50 dark:border-dm-border dark:text-dm-primary dark:hover:bg-dm-hover"
                    >
                      <Plus size={15} /> Conversație nouă
                    </button>
                    <div className="max-h-80 overflow-y-auto py-1">
                      {historyResults.length === 0 ? (
                        <p className="px-3 py-4 text-center text-xs text-gray-400 dark:text-dm-text-muted">{historyQuery ? 'Niciun rezultat.' : 'Nicio conversație salvată încă.'}</p>
                      ) : (
                        historyResults.map((c) => (
                          <div
                            key={c.id}
                            className={`group flex cursor-pointer items-start gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-dm-hover ${c.id === a.sessionId ? 'bg-primary/5 dark:bg-dm-primary/10' : ''}`}
                            onClick={() => {
                              a.loadConversation(c.id);
                              setHistoryOpen(false);
                            }}
                          >
                            <MessageSquare size={14} className="mt-0.5 shrink-0 text-gray-400 dark:text-dm-text-muted" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-baseline justify-between gap-2">
                                <p className="truncate text-sm font-medium text-gray-800 dark:text-dm-text">{c.title}</p>
                                <span className="shrink-0 text-[10px] text-gray-400 dark:text-dm-text-muted">{relativeTime(c.updatedAt)}</span>
                              </div>
                              {c.preview && <p className="truncate text-xs text-gray-400 dark:text-dm-text-muted">{c.preview}</p>}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                a.removeConversation(c.id);
                              }}
                              title="Șterge conversația"
                              className="shrink-0 rounded p-1 text-gray-300 opacity-0 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 dark:text-dm-text-muted dark:hover:bg-red-900/30"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              {confirmClear ? (
                <div className="flex items-center gap-1 text-[11px]">
                  <span className="text-gray-500 dark:text-dm-text-muted">Ștergi?</span>
                  <button
                    onClick={() => {
                      a.clear();
                      setConfirmClear(false);
                    }}
                    className="rounded-md bg-red-600 px-2 py-0.5 font-medium text-white transition-colors hover:bg-red-700"
                  >
                    Da
                  </button>
                  <button onClick={() => setConfirmClear(false)} className="rounded-md bg-gray-100 px-2 py-0.5 font-medium text-gray-600 transition-colors hover:bg-gray-200 dark:bg-dm-hover dark:text-dm-text">
                    Nu
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmClear(true)}
                  disabled={a.messages.length === 0}
                  title="Șterge conversația"
                  className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-500 disabled:opacity-40 dark:text-dm-text-muted dark:hover:bg-dm-hover"
                >
                  <Trash2 size={16} />
                </button>
              )}
              <button onClick={a.close} title="Închide (Esc)" className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-dm-text-muted dark:hover:bg-dm-hover">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <div ref={scrollRef} onScroll={handleScroll} className="absolute inset-0 space-y-2.5 overflow-y-auto bg-gray-50/80 px-3.5 py-3.5 dark:bg-dm-bg">
            {a.messages.length === 0 && (
              <div className="mt-10 text-center text-sm text-gray-400 dark:text-dm-text-muted">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-primary/5 text-darkprimary shadow-inner">
                  <Sparkles size={22} />
                </div>
                <p className="text-[15px] font-semibold text-gray-700 dark:text-dm-text">Cu ce te pot ajuta?</p>
                <p className="mx-auto mt-1.5 max-w-[330px] px-4 text-xs leading-relaxed">Caut prin clienți, bookinguri, containere, oferte, facturi, contracte și nave; fac statistici și rapoarte Excel/PDF, creez activități și actualizez clienți.</p>
                <div className="mt-4 flex flex-wrap justify-center gap-1.5 px-2">
                  {starterSuggestions(a.pageContext.entityType).map((s, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuick(s.prompt)}
                      className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-darkprimary dark:border-dm-border dark:bg-dm-surface dark:text-dm-text dark:hover:border-primary/60"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {a.messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`group relative max-w-[88%] px-3 py-2 text-sm shadow-sm ${
                    m.role === 'user'
                      ? 'rounded-2xl rounded-br-md bg-gradient-to-br from-primary to-darkprimary text-black'
                      : 'rounded-2xl rounded-bl-md border border-gray-200/80 bg-white text-gray-800 dark:border-dm-border dark:bg-dm-surface dark:text-dm-text'
                  }`}
                >
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none break-words dark:prose-invert prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-table:my-1">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {m.content || ''}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <span className="whitespace-pre-wrap">{m.content}</span>
                  )}
                  {m.role === 'assistant' && <ToolPills results={m.toolResults} />}
                  {m.role === 'assistant' && <EntityChips results={m.toolResults} />}
                  {m.role === 'assistant' && extractArtifacts(m.toolResults).map((art, i) => <ArtifactCard key={i} artifact={art} />)}
                  {m.role === 'assistant' && !!m.content && (
                    <button
                      onClick={() => copyMessage(m.id, m.content)}
                      title="Copiază răspunsul"
                      className={`absolute -right-2 -top-2 h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm dark:border-dm-border dark:bg-dm-surface ${
                        copiedId === m.id ? 'flex text-green-600 dark:text-green-400' : 'hidden text-gray-400 hover:text-primary group-hover:flex dark:text-dm-text-muted dark:hover:text-dm-primary'
                      }`}
                    >
                      {copiedId === m.id ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Sugestii de continuare (quick replies) */}
            {!a.isSending && !a.pendingAction && a.suggestedActions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {a.suggestedActions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => handleQuick(s.prompt)}
                    className="rounded-full border border-primary/40 bg-white px-2.5 py-0.5 text-[11px] font-medium text-gray-600 shadow-sm transition-colors hover:bg-primary/10 hover:text-darkprimary dark:border-primary/40 dark:bg-dm-surface dark:text-dm-text dark:hover:text-primary"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Card confirmare acțiune */}
            {a.pendingAction && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm shadow-sm dark:border-amber-700/60 dark:bg-amber-900/20">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-amber-800 dark:text-amber-300">Confirmare necesară</p>
                  {remainingLabel(a.pendingAction.expiresAt, now) && <span className="text-[11px] text-amber-600 dark:text-amber-300/80">valabil încă {remainingLabel(a.pendingAction.expiresAt, now)}</span>}
                </div>
                <p className="mt-1 whitespace-pre-wrap text-amber-700 dark:text-amber-200">{a.pendingAction.previewText}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={a.confirmPending}
                    disabled={a.isSending}
                    className="inline-flex items-center gap-1 rounded-md bg-green-600 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:opacity-60"
                  >
                    <Check size={12} /> Confirmă
                  </button>
                  <button onClick={a.cancelPending} className="rounded-md bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-50 dark:bg-dm-hover dark:text-dm-text dark:ring-dm-border">
                    Anulează
                  </button>
                </div>
              </div>
            )}

            {a.isSending && (
              <div className="flex justify-start">
                <div className="min-w-[240px] max-w-[92%] rounded-2xl rounded-bl-md border border-gray-200/80 bg-white px-3 py-2.5 shadow-sm dark:border-dm-border dark:bg-dm-surface">
                  <div className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-dm-text-muted">
                    <Loader2 size={13} className="shrink-0 animate-spin text-darkprimary dark:text-primary" />
                    <span className="font-medium">{a.liveMessage || 'Gândesc…'}</span>
                    {sendStart != null && now - sendStart >= 2000 && <span className="ml-auto text-[11px] opacity-70">{Math.floor((now - sendStart) / 1000)}s</span>}
                  </div>
                  {a.liveNotes.some((n) => n.trim()) && (
                    <div className="mt-2 space-y-2 border-t border-gray-100 pt-2 text-[13px] leading-snug text-gray-800 dark:border-dm-border dark:text-dm-text">
                      {a.liveNotes.filter((n) => n.trim()).map((note, i) => (
                        <p key={i} className="whitespace-pre-wrap">
                          {note}
                          {i === a.liveNotes.filter((n) => n.trim()).length - 1 && a.livePhase !== 'working' ? <span className="ml-0.5 inline-block h-3 w-0.5 animate-pulse bg-darkprimary align-middle dark:bg-primary" /> : null}
                        </p>
                      ))}
                    </div>
                  )}
                  {a.liveSteps.length > 0 && (
                    <details className="mt-2 border-t border-gray-100 pt-2 dark:border-dm-border" open={!a.liveNotes.some((n) => n.trim())}>
                      <summary className="cursor-pointer text-[11px] text-gray-400 dark:text-dm-text-muted">
                        {a.liveSteps.filter((s) => s.state === 'done').length}/{a.liveSteps.length} pași
                      </summary>
                      <ul className="mt-1.5 space-y-1">
                        {a.liveSteps.map((step) => (
                          <li key={step.id} className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-dm-text-muted">
                            {step.state === 'start' ? (
                              <Loader2 size={11} className="mt-0.5 shrink-0 animate-spin" />
                            ) : step.state === 'error' ? (
                              <XCircle size={11} className="mt-0.5 shrink-0 text-red-500" />
                            ) : (
                              <CheckCircle2 size={11} className="mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                            )}
                            <span>
                              {step.label}
                              {step.detail ? <span className="opacity-70"> · {step.detail}</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            )}
            </div>

            {/* Buton „sari la ultimul mesaj" — apare când userul e derulat în sus */}
            {!atBottom && (
              <button
                onClick={() => scrollToBottom()}
                title="Mergi la ultimul mesaj"
                className="absolute bottom-3 right-3.5 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-md transition-colors hover:text-darkprimary dark:border-dm-border dark:bg-dm-surface dark:text-dm-text-muted dark:hover:text-primary"
              >
                <ChevronDown size={15} />
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-white px-3 pb-2 pt-2.5 dark:border-dm-border dark:bg-dm-surface">
            <div className="mb-2 flex rounded-full border border-gray-200/80 bg-gray-50 p-0.5 dark:border-dm-border dark:bg-dm-bg" role="radiogroup" aria-label="Nivel complexitate răspuns">
              {COMPLEXITY_LEVELS.map((lvl) => {
                const active = a.complexity === lvl.id;
                return (
                  <button
                    key={lvl.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={lvl.title}
                    onClick={() => a.setComplexity(lvl.id)}
                    className={`flex-1 rounded-full px-2 py-1 text-[11px] font-semibold tracking-wide transition-all ${
                      active
                        ? 'bg-gradient-to-br from-primary to-darkprimary text-black shadow-sm'
                        : 'text-gray-500 hover:text-gray-800 dark:text-dm-text-muted dark:hover:text-dm-text'
                    }`}
                  >
                    {lvl.label}
                  </button>
                );
              })}
            </div>
            {/* Status dictare / eroare microfon */}
            {(voice.isRecording || voice.isTranscribing || voiceError) && (
              <div className="mb-2 flex items-center gap-1.5 text-xs">
                {voice.isRecording && (
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-red-600 dark:bg-red-400" />
                    Înregistrez… la stop apare tot textul
                  </span>
                )}
                {voice.isTranscribing && (
                  <span className="flex items-center gap-1.5 text-gray-500 dark:text-dm-text-muted">
                    <Loader2 size={12} className="animate-spin" /> Transcriu dictarea…
                  </span>
                )}
                {voiceError && !voice.isRecording && !voice.isTranscribing && <span className="text-red-600 dark:text-red-400">{voiceError}</span>}
              </div>
            )}
            <div
              className={`flex items-end gap-1 rounded-2xl border bg-white p-1.5 transition-all dark:bg-dm-bg ${
                voice.isRecording
                  ? 'border-red-300 ring-2 ring-red-100 dark:border-red-700 dark:ring-red-900/30'
                  : 'border-gray-200 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20 dark:border-dm-border'
              }`}
            >
              {voice.supported && (
                <button
                  onClick={handleMicToggle}
                  disabled={a.isSending || voice.isTranscribing}
                  title={voice.isRecording ? 'Oprește dictarea' : 'Dictează (vorbește în loc să scrii)'}
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors disabled:opacity-50 ${
                    voice.isRecording ? 'animate-pulse bg-red-600 text-white hover:bg-red-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:text-dm-text-muted dark:hover:bg-dm-hover'
                  }`}
                >
                  {voice.isTranscribing ? <Loader2 size={16} className="animate-spin" /> : voice.isRecording ? <Square size={14} /> : <Mic size={16} />}
                </button>
              )}
              <textarea
                ref={textareaRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={voice.isRecording ? 'Vorbește… textul se pune la stop' : voice.isTranscribing ? 'Transcriu dictarea…' : 'Scrie sau dictează un mesaj…'}
                className="max-h-40 flex-1 resize-none appearance-none border-0 bg-transparent px-2 py-1.5 text-sm text-gray-800 shadow-none outline-none placeholder:text-gray-400 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 dark:text-dm-text dark:placeholder:text-dm-text-muted"
              />
              {a.isSending ? (
                <button
                  onClick={() => {
                    const restored = a.cancelSend();
                    if (restored) setDraft((prev) => (prev.trim() ? prev : restored));
                  }}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-600 text-white shadow-sm transition-colors hover:bg-red-700"
                  title="Oprește răspunsul (Esc)"
                >
                  <Square size={14} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!draft.trim()}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-darkprimary text-black shadow-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:shadow-none"
                  title="Trimite (Enter)"
                >
                  <Send size={15} />
                </button>
              )}
            </div>
            <p className="mt-1 pr-1 text-right text-[10px] text-gray-300 dark:text-dm-text-muted">Enter trimite · Shift+↵ rând nou · Esc oprește</p>
          </div>
        </aside>
      )}
    </>,
    document.body,
  );
};

export default AssistantPanel;
